import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'

interface CounterProps {
    to: number
    prefix?: string
    suffix?: string
    className?: string
    duration?: number
}

// Counts up to a fixed figure the first time it is seen. The server renders the
// final figure, so the number is correct without JavaScript. Only for the
// studio's own static numbers, never for CMS values.
const Counter: FC<CounterProps> = ({ to, prefix = '', suffix = '', className, duration = 1.8 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            const state = { value: 0 }
            const render = (): void => {
                element.textContent = `${prefix}${Math.round(state.value)}${suffix}`
            }

            render()

            const tween = gsap.to(state, {
                value: to,
                duration,
                ease: 'expo.out',
                onUpdate: render,
                scrollTrigger: {
                    trigger: element,
                    start: 'top 92%',
                    once: true
                }
            })

            return () => {
                tween.kill()
                element.textContent = `${prefix}${to}${suffix}`
            }
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <span ref={ref} className={className}>
            {`${prefix}${to}${suffix}`}
        </span>
    )
}

export default Counter
